import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2, ShieldX } from "lucide-react";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
};

const isSuperAdminAccount = (user) =>
  Boolean(
    user?.isSuperAdmin ||
      user?.adminRole === "SuperAdmin" ||
      user?.role === "SuperAdmin"
  );

const isOrganizationAdminAccount = (user) =>
  Boolean(
    user?.isOrganizationAdmin ||
      user?.adminRole === "OrganizationAdmin" ||
      user?.role === "OrganizationAdmin"
  );

const OrganizationAdminRoute = ({ children }) => {
  const location = useLocation();
  const [state, setState] = useState({
    loading: true,
    allowed: false,
    redirectTo: "/login",
  });

  useEffect(() => {
    let cancelled = false;

    const checkAccess = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        if (!cancelled) {
          setState({
            loading: false,
            allowed: false,
            redirectTo: "/login",
          });
        }
        return;
      }

      const storedUser = readStoredUser();

      // Refus immédiat du Super Admin, même avant la requête réseau.
      if (isSuperAdminAccount(storedUser)) {
        if (!cancelled) {
          setState({
            loading: false,
            allowed: false,
            redirectTo: "/dashboard",
          });
        }
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json().catch(() => ({}));
        const currentUser = data?.user || null;

        if (!response.ok || !currentUser) {
          throw new Error(data?.message || "Authentication required");
        }

        localStorage.setItem("user", JSON.stringify(currentUser));

        const allowed =
          currentUser?.type === "Admin" &&
          currentUser?.status === "Active" &&
          !isSuperAdminAccount(currentUser) &&
          isOrganizationAdminAccount(currentUser);

        if (!cancelled) {
          setState({
            loading: false,
            allowed,
            redirectTo: allowed ? "" : "/dashboard",
          });
        }
      } catch {
        if (!cancelled) {
          setState({
            loading: false,
            allowed: false,
            redirectTo: "/login",
          });
        }
      }
    };

    checkAccess();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (state.loading) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center bg-slate-50 px-4">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-bold text-slate-700 shadow-sm">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          Vérification de l’accès…
        </div>
      </main>
    );
  }

  if (!state.allowed) {
    return (
      <Navigate
        to={state.redirectTo || "/dashboard"}
        replace
        state={{
          from: location,
          denied: true,
          code: "ORGANIZATION_ADMIN_REQUIRED",
        }}
      />
    );
  }

  return children;
};

export default OrganizationAdminRoute;
